package fr.frux.core.designsystem.icons

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import fr.frux.core.designsystem.theme.FruxExtendedColors
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors

// Regle de couleur des marqueurs (famille produit x phase), descendue de feature-map dans le design
// system pour que les listes d'arbres (tree-list, favoris, mes arbres) rendent la meme pastille que
// le marqueur correspondant sur la carte (TreeGlyphDisc), sans dependre de la feature carte.

/**
 * Famille de fruit affichee sur la plaque du marqueur (#204, amende #196 : la couleur portait le
 * statut, elle porte desormais la famille — la phase se lit par le traitement, cf.
 * [hybridMarkerTint]).
 */
enum class SpeciesFamily { POMME_POIRE, ROUGE, DORE, PRUNE_BAIES, COQUE, AUTRE }

/**
 * Resout la famille d'une espece a partir de son nom scientifique. Meme logique de cle que
 * [speciesGlyphKey] (genre + epithete pour les cas ambigus, sinon genre seul), mais une table de
 * decision distincte : la famille est une classification produit (« a quoi ca ressemble au
 * marche »), pas taxonomique, et ne colle pas toujours au regroupement des glyphes (ex. le coing,
 * `Cydonia`, partage le glyphe poire mais reste bien dans la famille Pommes&poires).
 *
 * Couvre les 29 especes de `SpeciesCatalog` (backend/backend-database/.../seed/SpeciesCatalog.kt)
 * sans trou, verifie une a une (cf. `TreeMarkerMappingTest`) :
 *
 * - **Pommes & poires** : `Malus domestica` (pommier), `Pyrus communis` (poirier),
 *   `Cydonia oblonga` (cognassier), `Crataegus germanica` (neflier — meme forme de fruit que la
 *   nefle/poire, cf. `speciesGlyphKey`).
 * - **Fruits rouges** : `Prunus avium` (cerise), `Rubus idaeus` (framboise), `Ribes rubrum`
 *   (groseille), `Crataegus monogyna` (cenelles — repli generique du genre, apres le cas
 *   `germanica` ci-dessus), `Rosa canina` (cynorhodon).
 * - **Fruits dores** : `Prunus armeniaca` (abricot), `Prunus persica` (peche), `Olea europaea`
 *   (olive — aucune autre famille ne lui va mieux ; huile/fruit dore).
 * - **Prunes & baies** : `Prunus domestica` (prune, repli generique du genre apres les cas
 *   cerise/abricot/peche/amande), `Prunus spinosa` (prunelle), `Vaccinium myrtillus` (myrtille),
 *   `Ribes nigrum` (cassis, repli generique du genre apres le cas groseille), `Sambucus nigra`
 *   (sureau, fruit ET fleur — la famille ne varie pas avec la partie recoltee), `Morus nigra`
 *   (murier noir), `Rubus fruticosus` et `Rubus` genre nu (mure/« murier des haies », repli
 *   generique apres le cas framboise), `Vitis vinifera` (raisin).
 * - **Fruits a coque** : `Prunus dulcis` (amande — avant le repli Prunus generique),
 *   `Juglans regia` (noix), `Corylus avellana` (noisette), `Castanea sativa` (chataigne).
 * - **Figues & autres** (repli produit par defaut) : `Ficus carica` (figue) et tout le reste —
 *   `Allium ursinum`, `Taraxacum officinale`, `Urtica dioica` (fleurs/feuilles/racines) ainsi que
 *   tout genre non catalogue.
 */
@Suppress("CyclomaticComplexMethod") // table de decision a plat, une branche par genre : la lire d'un bloc est le but
fun speciesFamilyKey(scientificName: String): SpeciesFamily {
    val tokens = scientificName.trim().split(' ').filter { it.isNotBlank() }
    val genus = tokens.getOrNull(0) ?: return SpeciesFamily.AUTRE
    val epithet = tokens.getOrNull(1)

    return when {
        genus.equals("Malus", ignoreCase = true) -> {
            SpeciesFamily.POMME_POIRE
        }

        genus.equals("Pyrus", ignoreCase = true) -> {
            SpeciesFamily.POMME_POIRE
        }

        genus.equals("Cydonia", ignoreCase = true) -> {
            SpeciesFamily.POMME_POIRE
        }

        genus.equals("Crataegus", ignoreCase = true) && epithet.equals("germanica", ignoreCase = true) -> {
            SpeciesFamily.POMME_POIRE
        }

        genus.equals("Prunus", ignoreCase = true) && epithet.equals("avium", ignoreCase = true) -> {
            SpeciesFamily.ROUGE
        }

        genus.equals("Rubus", ignoreCase = true) && epithet.equals("idaeus", ignoreCase = true) -> {
            SpeciesFamily.ROUGE
        }

        genus.equals("Ribes", ignoreCase = true) && epithet.equals("rubrum", ignoreCase = true) -> {
            SpeciesFamily.ROUGE
        }

        genus.equals("Rosa", ignoreCase = true) -> {
            SpeciesFamily.ROUGE
        }

        // Repli generique Crataegus (monogyna) : le cas germanica est deja traite plus haut.
        genus.equals("Crataegus", ignoreCase = true) -> {
            SpeciesFamily.ROUGE
        }

        genus.equals("Prunus", ignoreCase = true) &&
            (epithet.equals("armeniaca", ignoreCase = true) || epithet.equals("persica", ignoreCase = true)) -> {
            SpeciesFamily.DORE
        }

        genus.equals("Olea", ignoreCase = true) -> {
            SpeciesFamily.DORE
        }

        // Fruits a coque : avant le repli Prunus generique (Prunus dulcis = amande).
        genus.equals("Prunus", ignoreCase = true) && epithet.equals("dulcis", ignoreCase = true) -> {
            SpeciesFamily.COQUE
        }

        genus.equals("Juglans", ignoreCase = true) -> {
            SpeciesFamily.COQUE
        }

        genus.equals("Corylus", ignoreCase = true) -> {
            SpeciesFamily.COQUE
        }

        genus.equals("Castanea", ignoreCase = true) -> {
            SpeciesFamily.COQUE
        }

        // Prunes & baies : replis generiques des genres deja partiellement traites plus haut.
        genus.equals("Prunus", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Rubus", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Ribes", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Vaccinium", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Sambucus", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Morus", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        genus.equals("Vitis", ignoreCase = true) -> {
            SpeciesFamily.PRUNE_BAIES
        }

        // Figues & autres : Ficus, herbacees (Allium/Taraxacum/Urtica), et tout genre non catalogue.
        else -> {
            SpeciesFamily.AUTRE
        }
    }
}

/**
 * Teinte pleine d'une famille, extraite de [speciesFamilyColor] (#133) pour être réutilisée telle
 * quelle par la légende de la carte (`MapLegendSheet`) : un seul point de vérité entre le rendu
 * réel des marqueurs et son explication, jamais deux jeux de couleurs à maintenir en parallèle.
 */
fun SpeciesFamily.legendColor(colors: FruxExtendedColors): Color =
    when (this) {
        SpeciesFamily.POMME_POIRE -> colors.familyPomme
        SpeciesFamily.ROUGE -> colors.familyRouge
        SpeciesFamily.DORE -> colors.familyDore
        SpeciesFamily.PRUNE_BAIES -> colors.familyPrune
        SpeciesFamily.COQUE -> colors.familyCoque
        SpeciesFamily.AUTRE -> colors.familyAutre
    }

/** Fraction de lerp vers le blanc pour la teinte pâlie de la phase « bientôt mûr » (#204). */
const val SOON_PALE_FRACTION = 0.55f

/**
 * Traitement hybride famille × phase (#204) : la teinte de famille porte pleine en récolte, pâlie
 * en « bientôt mûr » (bord ambre en plus, cf. `MapMarker.soon`), et cède la place au gris neutre
 * hors saison / cueilli / inaccessible / inconnu — ces quatre derniers se confondent
 * délibérément, la distinction actionnable est ailleurs (fiche, liste).
 */
fun hybridMarkerTint(
    family: Color,
    status: FruxStatus,
    colors: FruxExtendedColors,
): Color =
    when (status) {
        FruxStatus.Available -> family

        FruxStatus.Flowering -> lerp(family, Color.White, SOON_PALE_FRACTION)

        FruxStatus.Picked,
        FruxStatus.OffSeason,
        FruxStatus.Unavailable,
        FruxStatus.Unknown,
        -> colors.statusUnknown
    }

/**
 * Teinte du marqueur d'un arbre sur la carte, telle que `toMapMarkers` la calcule (famille du nom
 * scientifique, modulee par le statut resolu). A utiliser partout ou une liste veut rendre la
 * pastille d'un arbre comme son marqueur ([fr.frux.core.designsystem.components.TreeGlyphDisc]).
 */
@Composable
fun treeMarkerTint(
    scientificName: String,
    status: FruxStatus,
): Color {
    val colors = MaterialTheme.fruxColors
    return hybridMarkerTint(speciesFamilyKey(scientificName).legendColor(colors), status, colors)
}
